import { FC, Suspense, useEffect } from 'react';
import {
  Link as ReactDomLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import AppLoading from '../components/App/AppLoading';
import AppLayout from '../layouts/AppLayout';

// Public
import Callback from './Auth/Callback';
import EmptyLogin from './Auth/EmptyLogin';
import Logout from './Auth/Logout';
import RefreshToken from './Auth/RefreshToken';

import { ROLES, ROUTES } from '../constants';
import { useAuthRouteInterceptor } from './routes';

// Dashboard
import Dashboard from './Dashboard/Dashboard';

// Onboard
import { NoTenantAccess } from './Auth/Onboard/NoTenantAccess';
import CreateUser from './Auth/Onboard/CreateUser';
import SelectTenant from './Auth/Onboard/SelectTenant';
import SwitchTenant from './Auth/Onboard/SwitchTenant';
import VerifyEmail from './Auth/Onboard/VerifyEmail';

// Accounts
import { AccountDetailsPage } from './AccountDetails/AccountDetailsPage';
import { SubscriptionOverview } from './AccountDetails/Subscriptions/SubscriptionOverview';
import Accounts from './Accounts/AccountListPage';
import { InvoicePage } from './Invoices/InvoicePage';

// Quotes
import { NewQuoteFormPage } from './Quotes/Quote/NewQuoteFormPage';
import { SalesLanding } from './Quotes/SalesLanding';

// Product Catalog
import { DiscountForm } from './ProductCatalog/Discounts/DiscountForm';
import DiscountListPage from './ProductCatalog/Discounts/DiscountListPage';
import { OfferingForm } from './ProductCatalog/Offerings/OfferingForm';
import OfferingListPage from './ProductCatalog/Offerings/OfferingListPage';
import { ProductForm } from './ProductCatalog/Products/ProductForm';
import ProductListPage from './ProductCatalog/Products/ProductListPage';
import { UsageTypeFormPage } from './ProductCatalog/UsageTypes/UsageTypeFormPage';
import UsageTypeListPage from './ProductCatalog/UsageTypes/UsageTypeListPage';

// Tenant Manage
import { ManageUsers } from './Tenants/ManageUsers/ManageUsers';
import { TenantPage } from './Tenants/TenantPage';

// Settings
import GuidedQuoting from './Settings/Quoting/GuidedQuoting/GuidedQuotingPage';
import QuoteBluePrintsPage from './Settings/Quoting/QuoteBluePrints/QuoteBluePrintsPage';
import QuoteSettings from './Settings/Quoting/QuoteSettings/QuoteSettings';
import { TeamsPage } from './Settings/Quoting/Teams/TeamsPage';

// Settings -> Billing
import { BillingSettings } from './Settings/Billing/BillingSettings/BillingSettings';
import { DunningPage } from './Settings/Billing/Dunning/DunningPage';
import EditDunning from './Settings/Billing/Dunning/EditDunning';
import { InvoiceSettings } from './Settings/Billing/Invoices/InvoiceSettings';
import { InvoiceTemplates } from './Settings/Billing/InvoiceTemplates/InvoiceTemplates';
import Pricing from './Settings/Billing/Pricing';
import Subscriptions from './Settings/Billing/Subscriptions';
import { TaxConnections } from './Settings/Billing/Tax/TaxConnections';

// Settings -> Integrations
import { CrmAuthCallback } from './Settings/Integration/Crm/CrmAuthCallback';
import Crm from './Settings/Integration/Crm/CrmConnections';
import PaymentProcessor from './Settings/Integration/PaymentProcessor/PaymentProcessor';
import EditWebhook from './Settings/Integration/Webhooks/EditWebhook';
import Webhooks from './Settings/Integration/Webhooks/Webhook';

// Settings -> Tenant
import { CustomFieldsPage } from './Settings/Tenant/CustomFields/CustomFieldsPage';
import { LegalEntitySettings } from './Settings/Tenant/LegalEntitySettings';
import { Notifications } from './Settings/Tenant/Notifications';

// CRM
import { CrmAmendCancelRenewContract } from './Crm/CrmAmendCancelRenewContract';

// Profile
import Profile from './Profile/Profile';

// Redirector
import Redirector from './Redirector';

import { EntityGroupEnum, RuleTypeEnum } from '@monetize/types/app';
import { AnonymousFeatureFlagWrapper } from '../components/App/AnonymousFeatureFlagWrapper';
import lazyWithPreload from '../components/App/LazyLoadRetry';
import ContractModal from '../components/Contracts/ContractModal';
import { MFlex, MLink, MPageLoader, MText } from '../components/Monetize';
import { AnonymousShareLayout } from '../layouts/AnonymousShareLayout';
import { AnonymousRecordShareWithCodeVerifierLayout } from '../layouts/AnonymousShareWithCodeVerifierLayout';
import { useAuth } from '../services/auth0';
import { useFlags } from '../services/launchDarkly';
import { QUOTE_SHARE_RULE_ROUTES } from './Settings/rules-v3/ruleRoutes';
import AccessDenied from './AccessDenied';

import { AppAIChatIcon } from '../components/App/AppAIChatIcon';
import { RenewalRouteWrapper } from '../components/RenewalModal/RenewalRouteWrapper';
import {
  BLUEPRINT_QUOTES_EDIT_ROLES,
  SETTINGS_TENANT_ROLES,
  SETTINGS_USERS_ROLES,
} from '../services/acl/roles';
import { AccountDocumentPage } from './AccountDetails/AccountDocuments/AccountDocumentPage';
import { BillGroupPage } from './AccountDetails/BillGroups/BillGroupPage';
import { ViewCreditNote } from './AccountDetails/Credits/CreditNotes/ViewCreditNote';
import { CreditPage } from './AccountDetails/Credits/Credits/CreditPage';
import { OneTimeInvoiceForm } from './AccountDetails/OneTimeInvoice/OneTimeInvoiceForm';
import { PaymentMethodsStripeCallback } from './AccountDetails/PaymentMethods/PaymentMethodsStripeCallback';
import { PaymentPage } from './AccountDetails/Payments/PaymentPage';
import { RefundPage } from './AccountDetails/Refunds/RefundPage';
import { SubscriptionUsageOverviewPage } from './AccountDetails/Subscriptions/Usage/SubscriptionUsageOverviewPage';
import { UsageEventsPage } from './AccountDetails/Usage/UsageEventsPage';
import { CustomerPortalAuthenticatedLayout } from './anonymousShare/customerPortal/CustomerPortalAuthenticatedLayout';
import { CustomerPortalLayout } from './anonymousShare/customerPortal/CustomerPortalLayout';
import { CustomerPortalPage } from './anonymousShare/customerPortal/CustomerPortalPage';
import { PaymentMethodSharePage } from './anonymousShare/PaymentMethodShare/PaymentMethodSharePage';
import { PaymentMethodShareStripeCallback } from './anonymousShare/PaymentMethodShare/PaymentMethodShareStripeCallback';
import { PrivateRoute } from './AppPrivateRoute';
import NotFound from './NotFound';
import { BillingPage } from './Billing/BillingPage';
import { CrmHubspotInitQuote } from './Crm/CrmHubspotInitQuote';
import ErrorPage from './ErrorPages/ErrorPage';
import { UnauthorizedPage } from './ErrorPages/UnauthorizedPage';
import IdPrefixNavigator from './IdPrefixNavigator';
import { InvoiceShareDetails } from './PayInvoice/InvoiceShareDetails';
import { StripePaymentCallback } from './PayInvoice/StripeCallback';
import { BucketFormPage } from './ProductCatalog/Buckets/BucketFromPage';
import BucketListPage from './ProductCatalog/Buckets/BucketListPage';
import { PylonJwtRedirector } from './PylonJwtRedirector';
import NewGuidedQuoteFormPage from './Quotes/Quote/components/guidedQuote/NewGuidedQuoteFormPage';
import { QuoteBaseRedirector } from './Quotes/Quote/QuoteBaseRedirector';
import { QuotePageWrapperContainer } from './Quotes/Quote/QuotePageWrapper';
import { QuoteSharePaymentMethodStripeCallback } from './QuoteShare/paymentMethod/QuoteSharePaymentMethodStripeCallback';
import { QuoteSharePage } from './QuoteShare/QuoteSharePage';
import { ReportPage } from './Report/ReportPage';
import { RevenueContractPage } from './RevenueRecognition/RevenueContractPage';
import { RevRecPage } from './RevenueRecognition/RevRecPage';
import { RevRecPageV2 } from './RevenueRecognition/RevRecPageV2';
import { Payments } from './Settings/Billing/Payments';
import { Accounting } from './Settings/Integration/Accounting/Accounting';
import { AdobeAcrobatSignCallback } from './Settings/Integration/Esign/components/AdobeAcrobatSignCallback';
import { DocusignCallback } from './Settings/Integration/Esign/components/DocusignCallback';
import Esign from './Settings/Integration/Esign/Esign';
import { IntegrationLog } from './Settings/Integration/IntegrationLog/IntegrationLog';
import { IntegrationLogDetailModalRoute } from './Settings/Integration/IntegrationLog/IntegrationLogDetailModal';
import { SlackCallback } from './Settings/Integration/Slack/Callback';
import Slack from './Settings/Integration/Slack/Slack';
import { SettingsLanding } from './Settings/LandingPage/SettingsLanding';
import { DealRoomSettings } from './Settings/Quoting/DealRoomSetting/DealRoomSetting';
import { QuoteExpirationReminder } from './Settings/Quoting/Expiration/QuoteExpirationReminder';
import { GuidedQuotingForm } from './Settings/Quoting/GuidedQuoting/GuidedQuotingForm';
import QuoteTemplates from './Settings/Quoting/QuoteTemplates';
import { RevRecSettings } from './Settings/RevenueRecognition/RevRecSettings';
import { SettingsSideNav } from './Settings/SideNavBar/SettingsSideNav';
import { ApiKeysPage } from './Settings/Tenant/ApiKeys.tsx/ApiKeysPage';
import { AuditLogs } from './Settings/Tenant/AuditLog/AuditLog';
import CurrencySettings from './Settings/Tenant/CurrencySettings';
import { CustomFieldsForm } from './Settings/Tenant/CustomFields/CustomFieldsForm';
import { LegalEntityForm } from './Settings/Tenant/LegalEntityForm';
import { TenantSettings } from './Tenants/TenantSettings/TenantSettings';

const TemplateEditor = lazyWithPreload(
  () => import('./Settings/Tenant/TemplateEditor/TemplateEditor'),
);

const InternalDealRoomPage = lazyWithPreload(
  () =>
    import('../dealroom/routes/DealRoom/InternalDealRoomPage/InternalDealRoomPage'),
);

const ExternalDealRoomPage = lazyWithPreload(
  () =>
    import('../dealroom/routes/DealRoom/ExternalDealRoomPage/ExternalDealRoomPage'),
);

const ApprovalRules = lazyWithPreload(
  () => import('./Settings/Approvals/ApprovalRules'),
);

const RecordAutomation = lazyWithPreload(
  () => import('./Settings/Billing/RecordAutomation/RecordAutomation'),
);

const ConditionalTermsPage = lazyWithPreload(
  () => import('./Settings/ConditionalTerms/ConditionalTermsPage'),
);

const ValidationRules = lazyWithPreload(
  () => import('./Settings/Validations/ValidationRules'),
);

const Onboarding = lazyWithPreload(
  () => import('./Settings/Tenant/Onboarding/OnboardingPage'),
);

const RuleFormPageV3 = lazyWithPreload(
  () => import('./Settings/rules-v3/RuleFormPage.v3'),
);

const QuoteShareSettings = lazyWithPreload(
  () => import('./Settings/Quoting/QuoteShare/QuoteShareSettings'),
);

const AuthOnlyRoute: FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth();

  useEffect(() => {
    // Wait for Auth0 to finish restoring any cached session before deciding
    // the user is logged out
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  if (isLoading || !isAuthenticated) {
    return <AppLoading />;
  }

  return <Outlet />;
};

/**
 * Flag-gated pages stay registered so a direct load of their URL does not fall
 * through to the catch-all route (and flash "Not Found") while LaunchDarkly is
 * still resolving the user's flags; the page is swapped for NotFound when off.
 */
const flagGated = (enabled: boolean | undefined, page: JSX.Element) =>
  enabled ? page : <NotFound />;

const AuthWrappedAppLayout = ({
  skipSidebar = false,
}: {
  skipSidebar?: boolean;
}) => {
  const { isReady, nextRoute = '' } = useAuthRouteInterceptor();
  const { isLDLoading } = useAuth();

  if (!isReady) {
    return (
      <AppLoading>
        <MText my="4" fontSize="sm">
          You can also{' '}
          <MLink fontSize="sm" asChild textDecor="underline">
            <ReactDomLink to={ROUTES.AUTH_LOGOUT}>log out</ReactDomLink>
          </MLink>
          .
        </MText>
      </AppLoading>
    );
  }

  if (nextRoute !== ROUTES.ROOT) {
    // Redirect to next routes
    return <Navigate to={nextRoute} replace />;
  }

  if (isLDLoading) {
    return (
      <AppLoading>
        <MText my="4" fontSize="sm">
          You can also{' '}
          <MLink fontSize="sm" asChild textDecor="underline">
            <ReactDomLink to={ROUTES.AUTH_LOGOUT}>log out</ReactDomLink>
          </MLink>
          .
        </MText>
      </AppLoading>
    );
  }

  if (skipSidebar) {
    return <Outlet />;
  }

  // If everything is good then go to the route
  return (
    <AppLayout>
      {/*
        Only show AI chat on authenticated routes
        this ensures that authenticated users visiting end-customer pages don't
        see the AI chat
       */}
      <AppAIChatIcon />
      <Outlet />
    </AppLayout>
  );
};

const AuthRouteSettingsLayout = () => (
  <SettingsSideNav>
    <Outlet />
  </SettingsSideNav>
);

export const AppRoutes: FC = () => {
  const navigate = useNavigate();
  const {
    metabaseReporting,
    revenueRecognition,
    useDealRoom,
    enableRevRecV2,
    pdfTemplateEditor,
  } = useFlags();

  return (
    <Suspense
      fallback={
        <MFlex justify="center" grow={1}>
          <MPageLoader />
        </MFlex>
      }
    >
      <Routes>
        <Route path={ROUTES.AUTH_LOGIN} element={<EmptyLogin />} />
        <Route path={ROUTES.AUTH_CALLBACK} element={<Callback />} />
        <Route path={ROUTES.AUTH_LOGOUT} element={<Logout />} />
        <Route path={ROUTES.AUTH_REFRESH_TOKEN} element={<RefreshToken />} />
        <Route path={ROUTES.REDIRECT} element={<Redirector />} />
        <Route path={ROUTES.PYLON_SSO} element={<PylonJwtRedirector />} />

        <Route
          path={ROUTES.SETTINGS_DOCUSIGN_CALLBACK}
          element={<DocusignCallback />}
        />

        <Route path="/onboard" element={<AuthOnlyRoute />}>
          <Route path={ROUTES.ONBOARD_CREATE_USER} element={<CreateUser />} />
          <Route
            path={ROUTES.ONBOARD_NO_TENANT_ACCESS}
            element={<NoTenantAccess />}
          />
          <Route path={ROUTES.ONBOARD_VERIFY_EMAIL} element={<VerifyEmail />} />
          <Route
            path={ROUTES.ONBOARD_SWITCH_TENANT}
            element={<SwitchTenant />}
          />
          <Route
            path={ROUTES.ONBOARD_SELECT_TENANT}
            element={<SelectTenant />}
          />
        </Route>

        <Route
          path={ROUTES.INVOICE_SHARE}
          element={
            <AnonymousFeatureFlagWrapper>
              <AnonymousShareLayout>
                <InvoiceShareDetails />
              </AnonymousShareLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />
        <Route
          path={ROUTES.INVOICE_SHARE_STRIPE_PAYMENT_CALLBACK}
          element={
            <AnonymousFeatureFlagWrapper>
              <StripePaymentCallback />
            </AnonymousFeatureFlagWrapper>
          }
        />

        <Route
          path={ROUTES.QUOTE_SHARE}
          element={
            <AnonymousFeatureFlagWrapper>
              <AnonymousRecordShareWithCodeVerifierLayout>
                <QuoteSharePage />
              </AnonymousRecordShareWithCodeVerifierLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />
        <Route
          path={ROUTES.QUOTE_SHARE_STRIPE_PAYMENT_CALLBACK}
          element={
            <AnonymousFeatureFlagWrapper>
              <AnonymousRecordShareWithCodeVerifierLayout>
                TODO
                {/* TODO: Add StripePaymentCallback */}
                {/* <StripePaymentCallback /> */}
              </AnonymousRecordShareWithCodeVerifierLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />
        <Route
          path={ROUTES.QUOTE_SHARE_PAYMENT_METHOD_STRIPE_CALLBACK}
          element={
            <AnonymousFeatureFlagWrapper>
              <QuoteSharePaymentMethodStripeCallback />
            </AnonymousFeatureFlagWrapper>
          }
        />

        <Route
          path={ROUTES.DEAL_ROOM_SHARE}
          element={
            <AnonymousFeatureFlagWrapper>
              <AnonymousRecordShareWithCodeVerifierLayout recordIdPathParamName="dealRoomId">
                <ExternalDealRoomPage />
              </AnonymousRecordShareWithCodeVerifierLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />

        <Route
          path={ROUTES.PAYMENT_METHOD_SHARE}
          element={
            <AnonymousFeatureFlagWrapper>
              <AnonymousRecordShareWithCodeVerifierLayout>
                <PaymentMethodSharePage />
              </AnonymousRecordShareWithCodeVerifierLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />
        <Route
          path={ROUTES.PAYMENT_METHOD_SHARE_STRIPE_CALLBACK}
          element={
            <AnonymousFeatureFlagWrapper>
              <PaymentMethodShareStripeCallback />
            </AnonymousFeatureFlagWrapper>
          }
        />

        {/* Route for non-authenticated (anonymous) user */}
        <Route
          path={ROUTES.CUSTOMER_PORTAL}
          element={
            <AnonymousFeatureFlagWrapper>
              <CustomerPortalLayout>
                {(sessionData, onLogout) => (
                  <CustomerPortalPage {...sessionData} onLogout={onLogout} />
                )}
              </CustomerPortalLayout>
            </AnonymousFeatureFlagWrapper>
          }
        />

        {/*
          Authenticated routes without application chrome (e.g. no sidebar)
        */}
        <Route element={<AuthWrappedAppLayout skipSidebar />}>
          {/* Preview customer portal for authenticated user */}
          <Route
            path={ROUTES.CUSTOMER_PORTAL_AUTHENTICATED_PREVIEW}
            element={
              <CustomerPortalAuthenticatedLayout>
                {(sessionData, onLogout) => (
                  <CustomerPortalPage {...sessionData} onLogout={onLogout} />
                )}
              </CustomerPortalAuthenticatedLayout>
            }
          />
        </Route>

        <Route path="/" element={<AuthWrappedAppLayout />}>
          <Route
            path={ROUTES.ROOT}
            element={<Navigate to={ROUTES.DASHBOARD} />}
          />
          <Route path={`${ROUTES.DASHBOARD}/*`} element={<Dashboard />} />

          {/* Accounts */}

          {/*
            This component makes POST requests on load, so it cannot be placed
            anywhere where a parent is re-rendered multiple times on load.
          */}
          <Route
            path={ROUTES.ACCOUNT_PAYMENT_METHODS_CALLBACK}
            element={<PaymentMethodsStripeCallback />}
          />

          <Route
            path={ROUTES.ACCOUNT_LIST}
            element={
              <PrivateRoute
                acls={[['accounts', 'read']]}
                component={<Accounts />}
              />
            }
          />
          <Route
            path={`${ROUTES.ACCOUNT_DETAIL}/*`}
            element={
              <PrivateRoute
                acls={[['accounts', 'read']]}
                component={<AccountDetailsPage />}
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_CREDITS_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<CreditPage />}
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_CREDIT_NOTE_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<ViewCreditNote />}
              />
            }
          />

          {/* Accounts Subscriptions */}
          <Route
            path={ROUTES.SUBSCRIPTION_OVERVIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<SubscriptionOverview />}
              />
            }
          />
          <Route
            path={`${ROUTES.BILL_GROUP_PAGE}/*`}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<BillGroupPage />}
              />
            }
          />
          <Route
            path={ROUTES.SUBSCRIPTION_PRODUCT_USAGE}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<SubscriptionUsageOverviewPage />}
              />
            }
          />

          {/* Usage */}
          <Route
            path={ROUTES.USAGE_EVENTS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<UsageEventsPage />}
              />
            }
          />

          {/* Payment */}
          <Route
            path={ROUTES.PAYMENTS_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<PaymentPage />}
              />
            }
          />

          <Route
            path={ROUTES.REFUND_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<RefundPage />}
              />
            }
          />

          {/* Invoice */}
          <Route
            path={ROUTES.INVOICE_DETAIL}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<InvoicePage />}
              />
            }
          />

          <Route
            path={ROUTES.INVOICE_DETAIL_CONTRACTS_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.ACCOUNTS_ROLES}
                component={
                  <ContractModal
                    isOpen
                    onClose={(params, source) => {
                      if (params?.invoiceId) {
                        navigate(
                          ROUTES.getInvoiceDetailRoute(params.invoiceId),
                          {
                            state: { source },
                          },
                        );
                      }
                    }}
                  />
                }
              />
            }
          />

          {/* One Time Invoice */}
          <Route
            path={ROUTES.ONE_TIME_INVOICE_CREATE}
            element={
              <PrivateRoute
                acls={[['billing', 'create']]}
                component={<OneTimeInvoiceForm />}
              />
            }
          />

          {/* Account Documents */}
          <Route
            path={`${ROUTES.ACCOUNT_DOCUMENT_PAGE}`}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<AccountDocumentPage />}
              />
            }
          />

          {/* Sales - Quotes, Contracts, Opportunities */}
          <Route
            path={`${ROUTES.SALES_LANDING}/*`}
            element={
              <PrivateRoute
                roles={ROLES.QUOTES_ROLES}
                component={<SalesLanding />}
              />
            }
          />

          <Route
            path={ROUTES.QUOTE_BASE}
            element={
              <PrivateRoute
                acls={[['sales', 'read']]}
                component={<QuoteBaseRedirector />}
              />
            }
          />
          <Route
            path={ROUTES.QUOTE_EDIT}
            element={
              <PrivateRoute
                acls={[['sales', 'read']]}
                component={<QuotePageWrapperContainer />}
              />
            }
          />
          <Route
            path={ROUTES.QUOTE_NEW}
            element={
              <PrivateRoute
                acls={[['sales', 'create']]}
                component={<NewQuoteFormPage />}
              />
            }
          />
          <Route
            path={ROUTES.QUOTE_NEW_GUIDED}
            element={
              <PrivateRoute
                acls={[['sales', 'create']]}
                component={<NewGuidedQuoteFormPage />}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_QUOTE_NEW}
            element={
              <PrivateRoute
                roles={ROLES.ACCOUNT_QUOTE_NEW_ROLES}
                component={<NewQuoteFormPage />}
              />
            }
          />
          <Route
            path={ROUTES.QUOTE_REVIEW}
            element={
              <PrivateRoute
                acls={[['sales', 'read']]}
                component={<QuotePageWrapperContainer />}
              />
            }
          >
            <Route
              path=":contractId"
              element={
                <PrivateRoute
                  roles={ROLES.ACCOUNTS_AND_QUOTES_ROLES}
                  component={
                    <ContractModal
                      isOpen
                      // The quote page remounts on every navigation
                      isContractSwitchedInPlace
                      onClose={(params) => {
                        if (params?.quoteId) {
                          navigate(ROUTES.getQuoteReviewRoute(params.quoteId), {
                            replace: true,
                          });
                        }
                      }}
                    />
                  }
                />
              }
            />
          </Route>

          {/* Products */}
          <Route
            path={ROUTES.PRODUCT_LIST}
            element={
              <PrivateRoute
                roles={ROLES.PRODUCT_LIST_ROLES}
                component={<ProductListPage />}
              />
            }
          />
          <Route
            path={ROUTES.PRODUCT_NEW}
            element={
              <PrivateRoute
                roles={ROLES.PRODUCT_NEW_ROLES}
                component={<ProductForm />}
              />
            }
          />
          <Route
            path={ROUTES.PRODUCT_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.PRODUCT_EDIT_ROLES}
                component={<ProductForm />}
              />
            }
          />

          {/* Offerings */}
          <Route
            path={ROUTES.OFFERING_LIST}
            element={
              <PrivateRoute
                roles={ROLES.OFFERING_LIST_ROLES}
                component={<OfferingListPage />}
              />
            }
          />
          <Route
            path={ROUTES.OFFERING_NEW}
            element={
              <PrivateRoute
                roles={ROLES.OFFERING_NEW_ROLES}
                component={<OfferingForm />}
              />
            }
          />
          <Route
            path={ROUTES.OFFERING_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.OFFERING_EDIT_ROLES}
                component={<OfferingForm />}
              />
            }
          />

          {/* Discount */}
          <Route
            path={ROUTES.DISCOUNT_LIST}
            element={
              <PrivateRoute
                roles={ROLES.DISCOUNT_LIST_ROLES}
                component={<DiscountListPage />}
              />
            }
          />
          <Route
            path={ROUTES.DISCOUNT_NEW}
            element={
              <PrivateRoute
                roles={ROLES.DISCOUNT_NEW_ROLES}
                component={<DiscountForm />}
              />
            }
          />
          <Route
            path={ROUTES.DISCOUNT_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.DISCOUNT_EDIT_ROLES}
                component={<DiscountForm />}
              />
            }
          />

          {/* Tenants */}
          <Route
            path={ROUTES.TENANT_LIST}
            element={
              <PrivateRoute
                roles={SETTINGS_TENANT_ROLES}
                component={<TenantPage />}
              />
            }
          />

          {/* Usage Types */}
          <Route
            path={ROUTES.USAGE_TYPE_LIST}
            element={
              <PrivateRoute
                roles={ROLES.USAGE_TYPE_LIST_ROLES}
                component={<UsageTypeListPage />}
              />
            }
          />
          <Route
            path={ROUTES.USAGE_TYPE_NEW}
            element={
              <PrivateRoute
                roles={ROLES.USAGE_TYPE_EDIT_ROLES}
                component={<UsageTypeFormPage />}
              />
            }
          />
          <Route
            path={ROUTES.USAGE_TYPE_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.USAGE_TYPE_EDIT_ROLES}
                component={<UsageTypeFormPage />}
              />
            }
          />

          {/* Buckets */}
          <Route
            path={ROUTES.BUCKET_LIST}
            element={
              <PrivateRoute
                roles={ROLES.BUCKET_LIST_ROLES}
                component={<BucketListPage />}
              />
            }
          />
          <Route
            path={ROUTES.BUCKET_NEW}
            element={
              <PrivateRoute
                roles={ROLES.BUCKET_NEW_ROLES}
                component={<BucketFormPage />}
              />
            }
          />
          <Route
            path={ROUTES.BUCKET_EDIT}
            element={
              <PrivateRoute
                roles={ROLES.BUCKET_EDIT_ROLES}
                component={<BucketFormPage />}
              />
            }
          />

          {/* Profile */}
          <Route path={ROUTES.PROFILE} element={<Profile />} />

          {/* Settings */}
          <Route path={ROUTES.SETTINGS} element={<SettingsLanding />} />

          {/* Settings->Billings */}
          <Route path="/settings" element={<AuthRouteSettingsLayout />}>
            <Route
              path={ROUTES.SETTINGS_TAX}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_TAX_ROLES}
                  component={<TaxConnections />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_REV_REC}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_REV_REC_ROLES}
                  component={<RevRecSettings />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_DUNNING_LIST}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_DUNNING_ROLES}
                  component={<DunningPage />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_DUNNING_NEW}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_DUNNING_ROLES}
                  component={<EditDunning />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_DUNNING_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_DUNNING_ROLES}
                  component={<EditDunning />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_SUBSCRIPTIONS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_SUBSCRIPTIONS_ROLE}
                  component={<Subscriptions />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_PRICING}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_PRICING_ROLE}
                  component={<Pricing />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_PAYMENTS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_PAYMENT_ROLE}
                  component={<Payments />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_INVOICES}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_INVOICES_ROLE}
                  component={<InvoiceSettings />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_INVOICE_TEMPLATES}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_INVOICE_TEMPLATES_ROLE}
                  component={<InvoiceTemplates />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_BILLING_SETTINGS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_INVOICE_TEMPLATES_ROLE}
                  component={<BillingSettings />}
                />
              }
            />

            {/* Settings->Quoting */}

            <Route
              path={ROUTES.SETTINGS_QUOTE_BLUEPRINTS}
              element={
                <PrivateRoute
                  roles={BLUEPRINT_QUOTES_EDIT_ROLES}
                  component={<QuoteBluePrintsPage />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_QUOTE_SETTINGS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_QUOTE_ROLES}
                  component={<QuoteSettings />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_QUOTE_SHARE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_QUOTE_ROLES}
                  component={<QuoteShareSettings />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_QUOTE_SHARE_RULE_CREATE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_RECORD_AUTOMATION_ROLES}
                  component={
                    <RuleFormPageV3
                      type={RuleTypeEnum.RECORD_AUTOMATION}
                      lockedTarget={EntityGroupEnum.QUOTE_SHARE}
                      routes={QUOTE_SHARE_RULE_ROUTES}
                    />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_QUOTE_SHARE_RULE_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_RECORD_AUTOMATION_ROLES}
                  component={
                    <RuleFormPageV3
                      type={RuleTypeEnum.RECORD_AUTOMATION}
                      lockedTarget={EntityGroupEnum.QUOTE_SHARE}
                      routes={QUOTE_SHARE_RULE_ROUTES}
                    />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_QUOTE_TEMPLATES}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_QUOTE_TEMPLATES_ROLES}
                  component={<QuoteTemplates />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_RECORD_AUTOMATION}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_RECORD_AUTOMATION_ROLES}
                  component={<RecordAutomation />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_RECORD_AUTOMATION_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_RECORD_AUTOMATION_ROLES}
                  component={
                    <RuleFormPageV3 type={RuleTypeEnum.RECORD_AUTOMATION} />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_RECORD_AUTOMATION_CREATE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_RECORD_AUTOMATION_ROLES}
                  component={
                    <RuleFormPageV3 type={RuleTypeEnum.RECORD_AUTOMATION} />
                  }
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_APPROVALS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={<ApprovalRules />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_APPROVALS_CREATE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.APPROVAL}
                    />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_APPROVALS_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.APPROVAL}
                    />
                  }
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_CONDITIONAL_TERMS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={<ConditionalTermsPage />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_CONDITIONAL_TERMS_CREATE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.CONDITIONAL_TERM}
                    />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_CONDITIONAL_TERMS_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.CONDITIONAL_TERM}
                    />
                  }
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_VALIDATIONS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={<ValidationRules />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_VALIDATIONS_CREATE}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.VALIDATION}
                    />
                  }
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_VALIDATIONS_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_VALIDATIONS_ROLES}
                  component={
                    <RuleFormPageV3
                      entity={EntityGroupEnum.QUOTE}
                      type={RuleTypeEnum.VALIDATION}
                    />
                  }
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_DEAL_ROOM}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_DEAL_ROOM_ROLES}
                  component={<DealRoomSettings />}
                />
              }
            />

            <Route
              path={`${ROUTES.SETTINGS_TEAMS}/*`}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_TEAMS_ROLES}
                  component={<TeamsPage />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_EMAILS}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_EMAIL_REMINDERS}
                  component={<QuoteExpirationReminder />}
                />
              }
            />
            <>
              <Route
                path={ROUTES.SETTINGS_GUIDED_QUOTING}
                element={
                  <PrivateRoute
                    roles={ROLES.ADMIN_ROLES}
                    component={<GuidedQuoting />}
                  />
                }
              />
              <Route
                path={`${ROUTES.SETTINGS_GUIDED_QUOTING_EDIT}/*`}
                element={
                  <PrivateRoute
                    roles={ROLES.ADMIN_ROLES}
                    component={<GuidedQuotingForm />}
                  />
                }
              />
              <Route
                path={`${ROUTES.SETTINGS_GUIDED_QUOTING_CREATE}/*`}
                element={
                  <PrivateRoute
                    roles={ROLES.ADMIN_ROLES}
                    component={<GuidedQuotingForm />}
                  />
                }
              />
            </>

            {/* END of Settings->Quoting */}

            {/* Settings->Integrations */}
            <Route
              path={ROUTES.SETTINGS_PAYMENT_PROCESSOR}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_PAYMENT_PROCESSOR_ROLES}
                  component={<PaymentProcessor />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_ESIGN}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_E_SIGN_ROLES}
                  component={<Esign />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_ADOBE_ACROBAT_SIGN_CALLBACK}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_E_SIGN_ROLES}
                  component={<AdobeAcrobatSignCallback />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_CRM}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_CRM_ROLES}
                  component={<Crm />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_CRM_CALLBACK}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_CRM_ROLES}
                  component={<CrmAuthCallback />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_WEBHOOK_LIST}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_WEBHOOK_ROLES}
                  component={<Webhooks />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_WEBHOOK_NEW}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_WEBHOOK_ROLES}
                  component={<EditWebhook />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_WEBHOOK_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_WEBHOOK_ROLES}
                  component={<EditWebhook />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_WEBHOOK_VIEW}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_WEBHOOK_ROLES}
                  component={<EditWebhook />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_ACCOUNTING}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_ACCOUNTING_ROLES}
                  component={<Accounting />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_INTEGRATION_LOG}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_INTEGRATION_LOG_ROLES}
                  component={<IntegrationLog />}
                />
              }
            >
              <Route
                path={ROUTES.SETTINGS_INTEGRATION_LOG_VIEW}
                element={<IntegrationLogDetailModalRoute />}
              />
            </Route>
            <Route
              path={ROUTES.SETTINGS_SLACK}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_SLACK_ROLES}
                  component={<Slack />}
                />
              }
            />
            <Route
              path={ROUTES.SETTINGS_SLACK_CALLBACK}
              element={
                <PrivateRoute
                  roles={ROLES.SETTINGS_SLACK_ROLES}
                  component={<SlackCallback />}
                />
              }
            />

            {/* END of Settings->Integrations */}

            {/* Settings->Tenant */}
            <Route
              path={ROUTES.API_KEYS}
              element={
                <PrivateRoute
                  roles={ROLES.API_KEYS_ROLES}
                  component={<ApiKeysPage />}
                />
              }
            />
            <Route
              path={ROUTES.COMPANY_SETTINGS}
              element={
                <PrivateRoute
                  roles={ROLES.COMPANY_SETTINGS_ROLES}
                  component={<LegalEntitySettings />}
                />
              }
            />
            <Route
              path={ROUTES.ONBOARDING}
              element={
                <PrivateRoute
                  roles={ROLES.BILLING_ADMIN_ROLES}
                  component={<Onboarding />}
                />
              }
            />
            <Route
              path={ROUTES.LEGAL_ENTITY_NEW}
              element={
                <PrivateRoute
                  roles={ROLES.COMPANY_SETTINGS_ROLES}
                  component={<LegalEntityForm />}
                />
              }
            />
            <Route
              path={ROUTES.LEGAL_ENTITY_EDIT}
              element={
                <PrivateRoute
                  roles={ROLES.COMPANY_SETTINGS_ROLES}
                  component={<LegalEntityForm />}
                />
              }
            />

            <Route
              path={ROUTES.TEMPLATE_EDITOR}
              element={
                <PrivateRoute
                  roles={ROLES.COMPANY_SETTINGS_ROLES}
                  component={flagGated(pdfTemplateEditor, <TemplateEditor />)}
                />
              }
            />

            {/* Settings->Tenant */}

            <Route
              path={ROUTES.CUSTOM_FIELDS_LIST}
              element={
                <PrivateRoute
                  acls={[['settings', 'read']]}
                  component={<CustomFieldsPage />}
                />
              }
            />
            <Route
              path={ROUTES.CUSTOM_FIELDS_EDIT}
              element={
                <PrivateRoute
                  acls={[['settings', 'create']]}
                  component={<CustomFieldsForm />}
                />
              }
            />
            <Route
              path={ROUTES.CUSTOM_FIELDS_NEW}
              element={
                <PrivateRoute
                  acls={[['settings', 'create']]}
                  component={<CustomFieldsForm />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_TENANT_SETTINGS}
              element={
                <PrivateRoute
                  roles={SETTINGS_TENANT_ROLES}
                  component={<TenantSettings />}
                />
              }
            />

            {/* Settings->Tenant user list */}
            <Route
              path={ROUTES.TENANT_DETAIL_USERS_LIST}
              element={
                <PrivateRoute
                  roles={SETTINGS_USERS_ROLES}
                  component={<ManageUsers />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_NOTIFICATIONS}
              element={
                <PrivateRoute
                  roles={ROLES.EMAIL_NOTIFICATIONS_ROLES}
                  component={<Notifications />}
                />
              }
            />

            {/* Settings->Tenant user list */}
            <Route
              path={ROUTES.SETTINGS_AUDIT_LOG}
              element={
                <PrivateRoute
                  roles={ROLES.ADMIN_ROLES}
                  component={<AuditLogs />}
                />
              }
            />

            <Route
              path={ROUTES.SETTINGS_CURRENCIES}
              element={
                <PrivateRoute
                  roles={ROLES.CURRENCY_SETTINGS_ROLES}
                  component={<CurrencySettings />}
                />
              }
            />

            {/* END of Settings->Tenant */}

            {/* Settings->Dealroom */}
            <Route
              path={ROUTES.SETTINGS_DEAL_ROOM}
              element={
                <PrivateRoute
                  roles={ROLES.CURRENCY_SETTINGS_ROLES}
                  component={flagGated(useDealRoom, <DealRoomSettings />)}
                />
              }
            />

            {/* END of Settings->Dealroom */}
          </Route>

          {/* CRM routes */}
          <Route
            path={ROUTES.CRM_AMEND_CANCEL_RENEW}
            element={
              <PrivateRoute
                roles={ROLES.QUOTES_EDIT_ROLES}
                component={<CrmAmendCancelRenewContract />}
              />
            }
          />
          <Route
            path={ROUTES.CONTRACT_RENEW}
            element={
              <PrivateRoute
                roles={ROLES.QUOTES_EDIT_ROLES}
                component={
                  <RenewalRouteWrapper
                    handleClose={(accountId: string) => {
                      navigate(ROUTES.getAccountContractsRoute(accountId), {
                        replace: true,
                      });
                    }}
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.CRM_HUBSPOT_NEW_QUOTE}
            element={
              <PrivateRoute
                roles={ROLES.QUOTES_EDIT_ROLES}
                component={<CrmHubspotInitQuote />}
              />
            }
          />

          {/* Reporting routes */}
          <Route
            path={`${ROUTES.REPORTING}`}
            element={
              <PrivateRoute
                roles={ROLES.REPORTING_ROLES}
                component={flagGated(metabaseReporting, <ReportPage />)}
              />
            }
          />

          {/* Revenue routes */}
          <Route
            path={`${ROUTES.REVENUE}`}
            element={
              <PrivateRoute
                roles={ROLES.BILLING_ADMIN_ROLES}
                component={flagGated(
                  revenueRecognition,
                  enableRevRecV2 ? <RevRecPageV2 /> : <RevRecPage />,
                )}
              />
            }
          />

          <Route
            path={`${ROUTES.REVENUE_CONTRACT_DETAIL}/*`}
            element={
              <PrivateRoute
                roles={ROLES.BILLING_ADMIN_ROLES}
                component={flagGated(
                  revenueRecognition,
                  <RevenueContractPage />,
                )}
              />
            }
          />

          <Route
            path={`${ROUTES.BILLING}/*`}
            element={
              <PrivateRoute
                roles={ROLES.BILLING_ADMIN_ROLES}
                component={<BillingPage />}
              />
            }
          />
          {/* DEAL ROOM */}
          <Route
            path={`${ROUTES.DEAL_ROOM}/:dealRoomId`}
            element={
              <PrivateRoute
                roles={ROLES.QUOTES_ROLES}
                component={<InternalDealRoomPage />}
              />
            }
          />
        </Route>

        <Route path={ROUTES.ACCESS_DENIED} element={<AccessDenied />} />
        <Route path="*" element={<IdPrefixNavigator />} />

        <Route path={ROUTES.ERROR_PAGE} element={<ErrorPage />} />
        <Route path={ROUTES.UNAUTHORIZED_PAGE} element={<UnauthorizedPage />} />
      </Routes>
    </Suspense>
  );
};
